import { useEffect, useCallback, useRef } from 'react';
import { updateAutoPath } from '../AutoPathRenderer';
import { updateVehiclePaths } from '../VehiclePathRenderer';
import { updateLocations } from '../MapMarkers';
import { updateDistanceLabels } from '../DistanceLabels';
import { 
  AUTO_PATH_LAYER, 
  CUSTOM_PATH_LAYER,
  UNFINISHED_PATH_LAYER,
  DISTANCE_LABELS_LAYER,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  POINTS_SOURCE
} from '../constants';

// Unified path management hook
export const useUnifiedPathManager = ({
  map,
  mapReady,
  sourcesReadyRef,
  isMountedRef,
  
  // Path data
  autoPath,
  customPath,
  vehiclePaths,
  locations,
  startId,
  
  // Display controls
  showAutoPath,
  showCustomPath,
  showUnfinishedPath,
  showDistanceLabels,
  
  // Vehicle related
  selectedVehicleIndex,
  pathComplete = false,
  customVehicleCount = 2
}) => {
  // Use ref to track last render state, avoid unnecessary re-renders
  const lastRenderState = useRef({
    autoPath: null,
    vehiclePaths: null,
    locations: null,
    visibility: {
      showAutoPath: false,
      showCustomPath: false,
      showUnfinishedPath: false,
      showDistanceLabels: false
    }
  });

  // Helper function to check if re-rendering is needed
  const shouldUpdatePath = useCallback((pathType, newData, oldData) => {
    if (!newData && !oldData) return false;
    if (!newData || !oldData) return true;
    
    // For location data, force update if we have data but map source is empty
    if (pathType === 'locations' && newData && newData.length > 0) {
      try {
        if (map.current && map.current.getSource(POINTS_SOURCE)) {
          const currentSourceData = map.current.getSource(POINTS_SOURCE)._data;
          if (!currentSourceData || !currentSourceData.features || currentSourceData.features.length === 0) {
            return true; // Force update - critical fix for location visibility
          }
        }
      } catch (e) {
        console.warn('[UnifiedPathManager] Error checking map source data:', e);
      }
    }
    
    // Simple deep comparison for path data
    try {
      return JSON.stringify(newData) !== JSON.stringify(oldData);
    } catch {
      // If serialization fails, default to update
      return true;
    }
  }, [map]);

  // Unified layer visibility management
  const manageLayerVisibility = useCallback(() => {
    if (!map.current || !mapReady || !sourcesReadyRef.current) {
      return;
    }

    try {
      // Auto path layer visibility
      if (map.current.getLayer(AUTO_PATH_LAYER)) {
        map.current.setLayoutProperty(
          AUTO_PATH_LAYER,
          'visibility',
          showAutoPath ? 'visible' : 'none'
        );
      }

      // Custom path layer - always hidden, as we use UnfinishedPathRenderer for unified handling
      if (map.current.getLayer(CUSTOM_PATH_LAYER)) {
        map.current.setLayoutProperty(
          CUSTOM_PATH_LAYER,
          'visibility',
          'none'
        );
      }

      // Unfinished/vehicle path layer visibility
      if (map.current.getLayer(UNFINISHED_PATH_LAYER)) {
        const shouldShowVehiclePath = (showUnfinishedPath && !pathComplete) || 
                                     (showCustomPath && pathComplete);
        map.current.setLayoutProperty(
          UNFINISHED_PATH_LAYER,
          'visibility',
          shouldShowVehiclePath ? 'visible' : 'none'
        );
      }

      // Distance labels layer visibility
      if (map.current.getLayer(DISTANCE_LABELS_LAYER)) {
        map.current.setLayoutProperty(
          DISTANCE_LABELS_LAYER,
          'visibility',
          showDistanceLabels ? 'visible' : 'none'
        );
      }

      // Critical fix: Ensure location layers are always visible
      // Location layers should be displayed in all modes
      const ensurePointLayersVisible = () => {
        if (map.current.getLayer(POINTS_CIRCLE_LAYER)) {
          map.current.setLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility', 'visible');
        } else {
          // Retry if layer not found (timing issue)
          setTimeout(() => {
            if (map.current && map.current.getLayer(POINTS_CIRCLE_LAYER)) {
              map.current.setLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility', 'visible');
            }
          }, 100);
        }

        if (map.current.getLayer(POINTS_SYMBOL_LAYER)) {
          map.current.setLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility', 'visible');
        } else {
          // Retry if layer not found (timing issue)
          setTimeout(() => {
            if (map.current && map.current.getLayer(POINTS_SYMBOL_LAYER)) {
              map.current.setLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility', 'visible');
            }
          }, 100);
        }
      };

      ensurePointLayersVisible();

      // Update visibility state
      lastRenderState.current.visibility = {
        showAutoPath,
        showCustomPath,
        showUnfinishedPath,
        showDistanceLabels
      };

    } catch (error) {
      console.error('[UnifiedPathManager] Error managing layer visibility:', error);
    }
  }, [map, mapReady, sourcesReadyRef, showAutoPath, showCustomPath, showUnfinishedPath, showDistanceLabels, pathComplete]);

  // Main path management Effect
  useEffect(() => {
    if (!isMountedRef.current || !map.current || !mapReady || !sourcesReadyRef.current) {
      return;
    }

    // Processing path updates

    try {
      // 1. Update location points (highest priority - other paths depend on locations)
      if (shouldUpdatePath('locations', locations, lastRenderState.current.locations)) {
        updateLocations({
          map: map.current,
          mapReady,
          sourcesReady: sourcesReadyRef.current,
          locations,
          startId
        });
        lastRenderState.current.locations = locations;
      }

      // 2. Update auto path
      if (showAutoPath && autoPath) {
        if (shouldUpdatePath('auto', autoPath, lastRenderState.current.autoPath)) {
          updateAutoPath({
            map: map.current,
            mapReady,
            sourcesReady: sourcesReadyRef.current,
            autoPath,
            locations,
            shouldShowAutoPath: true
          });
          lastRenderState.current.autoPath = autoPath;
        }
      } else if (lastRenderState.current.autoPath) {
        // Clear auto path
        updateAutoPath({
          map: map.current,
          mapReady,
          sourcesReady: sourcesReadyRef.current,
          autoPath: null,
          locations,
          shouldShowAutoPath: false
        });
        lastRenderState.current.autoPath = null;
      }

      // 3. Update vehicle paths (unified handling for completed and unfinished states)
      const shouldShowVehiclePath = (showUnfinishedPath && !pathComplete) || 
                                   (showCustomPath && pathComplete);
      
      if (shouldShowVehiclePath && vehiclePaths && vehiclePaths.length > 0) {
        if (shouldUpdatePath('vehicle', vehiclePaths, lastRenderState.current.vehiclePaths) ||
            lastRenderState.current.selectedVehicleIndex !== selectedVehicleIndex) {
          
          updateVehiclePaths({
            map: map.current,
            mapReady,
            sourcesReady: sourcesReadyRef.current,
            vehiclePaths,
            locations,
            selectedVehicleIndex,
            shouldShowPath: true,
            pathComplete
          });
          
          lastRenderState.current.vehiclePaths = vehiclePaths;
          lastRenderState.current.selectedVehicleIndex = selectedVehicleIndex;
        }
      } else if (lastRenderState.current.vehiclePaths) {
        // Clear vehicle paths
        updateVehiclePaths({
          map: map.current,
          mapReady,
          sourcesReady: sourcesReadyRef.current,
          vehiclePaths: [],
          locations,
          selectedVehicleIndex,
          shouldShowPath: false,
          pathComplete
        });
        lastRenderState.current.vehiclePaths = null;
      }

      // 4. Update distance labels (only when needed)
      if (showDistanceLabels) {
        const hasPathData = (showAutoPath && autoPath) || 
                           (shouldShowVehiclePath && vehiclePaths && vehiclePaths.length > 0);
        
        if (hasPathData) {
          updateDistanceLabels({
            map: map.current,
            mapReady,
            sourcesReady: sourcesReadyRef.current,
            shouldShowDistanceLabels: true,
            shouldShowAutoPath: showAutoPath,
            shouldShowCustomPath: showCustomPath,
            autoPath,
            customPath,
            locations,
            vehiclePaths: vehiclePaths || null
          });
        }
      }

    } catch (error) {
      console.error('[UnifiedPathManager] Error in path management:', error);
    }

  }, [
    // Core dependencies
    map, mapReady, sourcesReadyRef, isMountedRef,
    
    // Data dependencies
    autoPath, vehiclePaths, locations, startId,
    
    // Display control dependencies
    showAutoPath, showCustomPath, showUnfinishedPath, showDistanceLabels,
    
    // Vehicle related dependencies
    selectedVehicleIndex, pathComplete, customVehicleCount,
    
    // Helper functions
    shouldUpdatePath
  ]);

  // Layer visibility management Effect (independent of data updates)
  useEffect(() => {
    manageLayerVisibility();
  }, [manageLayerVisibility]);

  // Return useful state information (optional)
  return {
    isReady: mapReady && sourcesReadyRef.current,
    hasAutoPath: !!lastRenderState.current.autoPath,
    hasVehiclePaths: !!lastRenderState.current.vehiclePaths,
    lastUpdate: lastRenderState.current
  };
};
